/* Agatha redesign: header behaviour (navigation builder).
   - Mega panels: open on hover (120 ms intent) or keyboard focus at >= 1100 px, close on leave
     (150 ms), Escape, outside click or when the sticky header hides. On touch screens the first
     tap on a department opens its panel and the second follows the link.
   - Phone/tablet menu (#AgMenuDrawer): full-screen dialog with focus kept inside, Escape, scroll lock.
   - Age links ([data-ag-age]) point to <clothing>?ag_age=<id> (collection JS applies the filter);
     the remembered age (Agatha.getAge) is marked, and a click remembers the new one.
   - Phones: the search row slides away while scrolling down and slides back on the way up. */
(function () {
  'use strict';

  var DESK = window.matchMedia('(min-width: 1100px)');
  var PHONE = window.matchMedia('(max-width: 749px)');

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function setHeaderHeight() {
    var section = document.querySelector('.section-header');
    if (section) document.documentElement.style.setProperty('--header-height', section.offsetHeight + 'px');
  }

  /* ---------------------------------------------------------------- mega panels */
  function initMega(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-ag-mega]'));
    if (!items.length) return;

    function close(item) {
      clearTimeout(item._agTimer);
      item.classList.remove('is-open');
      var link = item.querySelector('.ag-nav__link');
      if (link) link.setAttribute('aria-expanded', 'false');
    }
    function closeAll(except) {
      items.forEach(function (i) {
        if (i !== except) close(i);
      });
    }
    function open(item) {
      clearTimeout(item._agTimer);
      closeAll(item);
      // The panel's max-height is 100vh minus --header-height, which Dawn measures only once, when
      // the header connects. A wrong reading (an unstyled header measures about 5,000 px) shrinks
      // panels to their padding, as Rony saw on 30 Sept (#101). Measure again now.
      setHeaderHeight();
      item.classList.add('is-open');
      var link = item.querySelector('.ag-nav__link');
      if (link) link.setAttribute('aria-expanded', 'true');
    }

    items.forEach(function (item) {
      var link = item.querySelector('.ag-nav__link');
      var lastPointer = '';
      // Mouse: hover intent. Touch and pen use the first-tap-opens rule below.
      item.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse' || !DESK.matches) return;
        clearTimeout(item._agTimer);
        var anyOpen = items.some(function (i) {
          return i.classList.contains('is-open');
        });
        // Moving along the row with a panel already open switches at once.
        item._agTimer = setTimeout(function () {
          open(item);
        }, anyOpen ? 0 : 120);
      });
      item.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse' || !DESK.matches) return;
        clearTimeout(item._agTimer);
        item._agTimer = setTimeout(function () {
          close(item);
        }, 150);
      });
      item.addEventListener('focusin', function (e) {
        if (!DESK.matches) return;
        if (item._agSuppress) {
          item._agSuppress = false;
          return;
        }
        if (e.target === link && !link.matches(':focus-visible')) return;
        open(item);
      });
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) close(item);
      });
      link.addEventListener('pointerdown', function (e) {
        lastPointer = e.pointerType;
      });
      link.addEventListener('click', function (e) {
        // Touch screens at desktop width: the first tap opens the panel, the second follows the link.
        var touch = lastPointer === 'touch' || lastPointer === 'pen';
        lastPointer = '';
        if (DESK.matches && touch && !item.classList.contains('is-open')) {
          e.preventDefault();
          open(item);
        }
      });
      link.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') {
          var first = item.querySelector('.ag-mega a');
          if (first) {
            e.preventDefault();
            open(item);
            first.focus();
          }
        }
      });
    });

    // Panel pictures are lazy; fetch them as soon as the pointer or focus reaches the row,
    // so a panel opens with its pictures already there.
    var warmed = false;
    function warm() {
      if (warmed) return;
      warmed = true;
      root.querySelectorAll('.ag-mega img[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
    }
    var row = root.querySelector('.ag-nav');
    if (row) {
      row.addEventListener('pointerenter', warm);
      row.addEventListener('focusin', warm);
    }

    var list = root.querySelector('.ag-nav__list');
    if (list) {
      list.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        var links = Array.prototype.slice.call(list.querySelectorAll(':scope > li > .ag-nav__link'));
        var i = links.indexOf(document.activeElement);
        if (i < 0) return;
        e.preventDefault();
        links[(i + (e.key === 'ArrowRight' ? 1 : links.length - 1)) % links.length].focus();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var openItem = items.filter(function (i) {
        return i.classList.contains('is-open');
      })[0];
      if (!openItem) return;
      var focusInside = openItem.contains(document.activeElement);
      close(openItem);
      if (focusInside) {
        var l = openItem.querySelector('.ag-nav__link');
        // Escape returns focus to the department link without reopening its panel.
        openItem._agSuppress = document.activeElement !== l;
        l.focus();
      }
    });
    document.addEventListener('click', function (e) {
      if (!e.target.closest || !e.target.closest('[data-ag-mega]')) closeAll();
    });
    document.addEventListener('ag:header-hide', function () {
      closeAll();
    });
    DESK.addEventListener('change', function () {
      closeAll();
    });
  }

  /* ---------------------------------------------------------------- phone / tablet menu */
  function initDrawer() {
    var drawer = document.getElementById('AgMenuDrawer');
    if (!drawer) return;
    var openers = Array.prototype.slice.call(document.querySelectorAll('[data-ag-drawer-open]'));
    var lastOpener = null;

    function focusables() {
      return Array.prototype.slice
        .call(drawer.querySelectorAll('a[href], button:not([disabled]), summary, input, select, [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) {
          return el.offsetParent !== null || el === document.activeElement;
        });
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === drawer)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    function open(opener) {
      lastOpener = opener || null;
      drawer.hidden = false;
      drawer.classList.add('is-open');
      document.body.classList.add('ag-lock');
      openers.forEach(function (b) {
        b.setAttribute('aria-expanded', 'true');
      });
      document.addEventListener('keydown', onKey);
      var closeBtn = drawer.querySelector('[data-ag-drawer-close]');
      (closeBtn || drawer).focus();
    }
    function close() {
      if (drawer.hidden) return;
      drawer.classList.remove('is-open');
      drawer.hidden = true;
      document.body.classList.remove('ag-lock');
      openers.forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
      });
      document.removeEventListener('keydown', onKey);
      if (lastOpener && lastOpener.offsetParent !== null) lastOpener.focus();
    }

    openers.forEach(function (b) {
      b.addEventListener('click', function () {
        if (drawer.hidden) open(b);
        else close();
      });
    });
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-ag-drawer-close]')) close();
    });
    DESK.addEventListener('change', function (m) {
      if (m.matches) close();
    });
    // Reopen state after the back button (bfcache): start closed.
    window.addEventListener('pageshow', function () {
      close();
    });
    window.AgathaHeader = window.AgathaHeader || {};
    window.AgathaHeader.openMenu = function () {
      open(null);
    };
    window.AgathaHeader.closeMenu = close;
  }

  /* ---------------------------------------------------------------- remembered age */
  function currentAge() {
    try {
      if (window.Agatha && typeof window.Agatha.getAge === 'function') return window.Agatha.getAge() || '';
    } catch (e) {
      /* fall through */
    }
    try {
      var raw = window.localStorage.getItem('agatha-age');
      if (!raw) return '';
      try {
        var v = JSON.parse(raw);
        return typeof v === 'string' ? v : '';
      } catch (e) {
        return raw;
      }
    } catch (e) {
      return '';
    }
  }
  function markAges() {
    var age = currentAge();
    document.querySelectorAll('[data-ag-age]').forEach(function (a) {
      var on = !!age && a.getAttribute('data-ag-age') === age;
      a.classList.toggle('is-on', on);
      if (on) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    });
  }
  function initAges() {
    markAges();
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[data-ag-age]');
      if (!a) return;
      try {
        if (window.Agatha && typeof window.Agatha.setAge === 'function') window.Agatha.setAge(a.getAttribute('data-ag-age'));
      } catch (err) {
        /* the collection page reads ?ag_age= anyway */
      }
    });
    window.addEventListener('storage', function (e) {
      if (e.key === 'agatha-age') markAges();
    });
    ['agatha:age', 'agatha:agechange'].forEach(function (name) {
      window.addEventListener(name, markAges);
      document.addEventListener(name, markAges);
    });
    window.AgathaHeader = window.AgathaHeader || {};
    window.AgathaHeader.markAges = markAges;
  }

  /* ---------------------------------------------------------------- phone: fold the search row */
  function initCompact(root) {
    var lastY = window.scrollY;
    var input = root.querySelector('.ag-search__input');
    var ticking = false;
    // Integration fix: folding or unfolding the search row changes the sticky header's height, and
    // the browser's scroll anchoring then moves the page by that height. That scroll used to fold or
    // unfold the row again, forever (the page shook on phones once scrolled). Ignore the scroll the
    // toggle itself causes, for as long as the slide lasts.
    // The row slides over FOLD_MS (the .ag-search transition in agatha-header.css). With reduced
    // motion it stays open: folding without the slide brings back the jump.
    var FOLD_MS = 280;
    var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
    var settleUntil = 0;
    var foldTimer = 0;
    function toggleCompact(on) {
      clearTimeout(foldTimer);
      root.classList.toggle('ag-header--compact', on);
      // Unfolding: the row stays clipped until it has grown back (agatha-header.css).
      root.classList.toggle('ag-header--unfolding', !on);
      settleUntil = Date.now() + FOLD_MS + 150;
      foldTimer = setTimeout(function () {
        root.classList.remove('ag-header--unfolding');
        setHeaderHeight();
      }, FOLD_MS + 20);
    }
    function update() {
      ticking = false;
      var y = window.scrollY;
      if (Date.now() < settleUntil) {
        lastY = y;
        return;
      }
      if (!PHONE.matches) {
        if (root.classList.contains('ag-header--compact')) {
          clearTimeout(foldTimer);
          root.classList.remove('ag-header--compact', 'ag-header--unfolding');
          setHeaderHeight();
        }
        lastY = y;
        return;
      }
      if (Math.abs(y - lastY) < 6) return;
      var searching = input && document.activeElement === input;
      var compact = y > 160 && y > lastY && !searching && !REDUCE.matches;
      if (root.classList.contains('ag-header--compact') !== compact) toggleCompact(compact);
      lastY = y;
    }
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    if (input) {
      input.addEventListener('focus', function () {
        if (root.classList.contains('ag-header--compact')) toggleCompact(false);
      });
    }
    PHONE.addEventListener('change', update);
  }

  /* ---------------------------------------------------------------- language switch */
  // Size filter values are labels of the current language ("2 ans" / "2T"): carried across a
  // language switch they would match nothing, so the switch drops filter.v.option.* and keeps
  // the rest of the address (ag_age, availability, price, sort, page).
  // Navigation fix (FLOW-1, G1): return_to is built from the live address at switch time. The
  // value Liquid prints is the address the page loaded with; Dawn's facets change the address in
  // place (history.pushState) without re-rendering the header or footer, so that value goes stale.
  // Covers the top bar and phone menu pills (submit event) and Dawn's footer <localization-form>,
  // which calls form.submit() (no submit event) from its own click handler: a capture-phase click
  // listener runs first.
  function currentLocale() {
    var l = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || '';
    return String(l).toLowerCase().split('-')[0];
  }
  function liveReturnTo(target) {
    var url = new URL(window.location.pathname + window.location.search + window.location.hash, window.location.origin);
    var hadQuery = url.search.length > 1;
    if (target && String(target).toLowerCase().split('-')[0] !== currentLocale()) {
      var stripped = false;
      Array.from(url.searchParams.keys()).forEach(function (k) {
        if (k.indexOf('filter.v.option.') === 0) {
          url.searchParams.delete(k);
          stripped = true;
        }
      });
      // Integration fix 2: carry the age the collection page shows (agatha-collection.js keeps
      // data-ag-active-age current after in-place updates) so the other language applies it
      // with its own size labels.
      if (stripped && !url.searchParams.get('ag_age')) {
        var plp = document.querySelector('[data-ag-plp]');
        var age = (plp && plp.getAttribute('data-ag-active-age')) || '';
        if (age) url.searchParams.set('ag_age', age);
      }
    }
    // Shopify's /localization copies the Referer's query (the old labels) whenever return_to has
    // none. When the address had a query and nothing is left, send an empty ag_age, which the
    // collection script removes.
    if (hadQuery && !url.searchParams.toString()) url.searchParams.set('ag_age', '');
    var qs = url.searchParams.toString();
    return url.pathname + (qs ? '?' + qs : '') + url.hash;
  }
  function fixReturnTo(form, target) {
    if (!form) return;
    var input = form.querySelector('input[name="return_to"]');
    if (!input) return;
    try {
      input.value = liveReturnTo(target);
    } catch (err) {
      /* keep Shopify's own return_to */
    }
  }
  function isLanguageForm(form) {
    return !!(
      form &&
      form.tagName === 'FORM' &&
      (form.getAttribute('action') || '').indexOf('/localization') > -1 &&
      form.querySelector('[name="locale_code"]')
    );
  }
  function initLanguage() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!isLanguageForm(form)) return;
      var btn = e.submitter;
      var target =
        (btn && btn.name === 'locale_code' && btn.value) ||
        form._agLocale ||
        (form.querySelector('input[name="locale_code"]') || {}).value;
      fixReturnTo(form, target);
    });
    document.addEventListener(
      'click',
      function (e) {
        if (!e.target.closest) return;
        // The FR | EN pills: remember the pressed language for browsers without e.submitter.
        var btn = e.target.closest('button[name="locale_code"]');
        if (btn && isLanguageForm(btn.form)) btn.form._agLocale = btn.value;
        // Dawn's language list (footer): <localization-form> sets locale_code from the item's
        // data-value, then calls form.submit(), which fires no submit event.
        var a = e.target.closest('localization-form a[data-value]');
        if (!a) return;
        var form = a.closest('form');
        if (!isLanguageForm(form)) return;
        fixReturnTo(form, a.getAttribute('data-value'));
      },
      true
    );
  }

  /* ---------------------------------------------------------------- phone search placeholder */
  // Navigation fix (F06): the full placeholder does not fit the phone search field in French.
  // Liquid prints the short one, so phones show it from the first paint (the cut-off long one
  // stayed visible until this script ran on slow connections); wider screens get the long one.
  function initPlaceholder(root) {
    var input = root.querySelector('.ag-search__input[data-ag-placeholder-long]');
    if (!input) return;
    var short = input.getAttribute('placeholder') || '';
    var long = input.getAttribute('data-ag-placeholder-long') || short;
    function apply() {
      input.setAttribute('placeholder', PHONE.matches ? short : long);
    }
    apply();
    PHONE.addEventListener('change', apply);
  }

  ready(function () {
    initLanguage();
    var root = document.querySelector('[data-ag-header]');
    if (!root) return;
    initPlaceholder(root);
    initMega(root);
    initDrawer();
    initAges();
    initCompact(root);
    // Keep --header-height current for everything that reads it, not only the mega panels.
    setHeaderHeight();
    window.addEventListener('load', setHeaderHeight);
    var resizing = false;
    window.addEventListener('resize', function () {
      if (resizing) return;
      resizing = true;
      requestAnimationFrame(function () {
        resizing = false;
        setHeaderHeight();
      });
    });
  });
})();
